'use client'

import { faBriefcase } from '@document-crunch/pro-regular-svg-icons'
import { Icon } from '@/dc-shared-web/src/components/base/Icon'
import { Tooltip, ProjectNew, ErrorPage } from '@/dc-shared-web/src'
import { ProjectActions } from '@/app/(dashboard)/projects/ProjectActions'
import { Folder } from '@/dc-shared-web/src/hooks/api/folder/types'
import { usePathWithParams } from '@/app/core/hooks/usePathWithParams'
import { useSearchParams } from 'next/navigation'
import { ListItemCard } from '@/app/(dashboard)/ListItemCard'
import { LocationType } from '@/app/(dashboard)/projects/FolderSelectionTree'
import { useIsProjectEditor } from '@/dc-shared-web/src/hooks/api/user/useIsProjectEditor'
import { useGetProjects } from '@/dc-shared-web/src/hooks/api/project'

export const ProjectsList = ({
  teamId,
  shouldShowActions,
  page = 'dashboard',
  parentFolder,
}: {
  teamId: string
  shouldShowActions: boolean
  page?: 'folder' | 'dashboard'
  parentFolder?: Folder
}) => {
  const { data: projects, isLoading: isLoadingProjects } = useGetProjects({
    teamUUID: teamId,
    folderUUID: parentFolder?.uuid,
    nameQuery: undefined,
    limit: 500,
    parentDash: !parentFolder,
  })

  const filteredProjects = projects?.pages?.flatMap((page) =>
    page?.results?.filter((project) => !project.folderId)
  )

  const getInitialLocation = (): LocationType => {
    if (page === 'folder') {
      return {
        type: 'folder',
        id: parentFolder?.uuid || '',
        name: parentFolder?.name || '',
      }
    }

    return {
      type: 'dashboard',
      id: 'dashboard',
      name: 'Dashboard',
    }
  }

  const isProjectEditor = useIsProjectEditor()

  /*
    If there are no projects, don't render anything.
    CC-587 adds inline error handling
    */

  if (!filteredProjects || filteredProjects.length === 0 || isLoadingProjects) {
    if (isProjectEditor && !isLoadingProjects && page === 'dashboard') {
      return <ErrorPage errorType='projectEditorUnassigned' />
    }
    return null
  }

  return (
    <Layout>
      <div
        data-testid='projects-list'
        className='tw-grid tw-gap-3 tw-grid-cols-[repeat(auto-fill,minmax(220px,1fr))]'
      >
        {filteredProjects?.map((project) => (
          <ProjectCard
            key={project.uuid}
            project={project}
            shouldShowActions={shouldShowActions}
            initialLocation={getInitialLocation()}
          />
        ))}
      </div>
    </Layout>
  )
}

const Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div>
      <h2 className='tw-text-md tw-font-medium'>Projects</h2>
      <div className='tw-mt-4'>{children}</div>
    </div>
  )
}

const ProjectCard = ({
  project,
  shouldShowActions,
  initialLocation,
}: {
  project: ProjectNew
  shouldShowActions: boolean
  initialLocation: LocationType
}) => {
  const projectDetailsPath = usePathWithParams(`/projects/${project.uuid}`)

  const identifier = project.identifier ? `#${project.identifier}` : ''
  const isNew = useSearchParams().get('newProjectId') === project.uuid

  return (
    <>
      <ListItemCard
        data-testid='project'
        href={projectDetailsPath}
        title={project.name}
        description={
          <>
            {identifier ? (
              <div className='-tw-mt-0.5 tw-text-xs tw-text-neutral-600 tw-truncate tw-pl-7.5'>
                <Tooltip content='Project number' withArrow>
                  <span>{identifier}</span>
                </Tooltip>
              </div>
            ) : (
              <div className='-tw-mt-0.5 tw-text-xs tw-text-neutral-600 tw-truncate tw-pl-7.5 tw-h-4'></div>
            )}
          </>
        }
        icon={
          <Icon icon={faBriefcase} size='1x' className='tw-text-foreground' />
        }
        rightSlot={
          shouldShowActions ? (
            <ProjectActions
              project={project}
              initialLocation={initialLocation}
            />
          ) : null
        }
        isNew={isNew}
      />
    </>
  )
}
